import clsx from "clsx";
import { ReactNode } from "react";
import { NavLink } from "react-router";

import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";

interface SegmentedControlOption {
  /** Route the segment switches to; also its React key. */
  to: string;
  /** Only match this exact path, not its sub-routes. */
  end?: boolean;
  icon: ReactNode;
  /** Names the segment for screen readers - the segments are icon-only. */
  ariaLabel: string;
}

interface Props {
  className?: string;
  options: SegmentedControlOption[];
}

/**
 * The design system's `dire-segmented-control`: a small group of mutually
 * exclusive segments in a tinted track, used to switch between two
 * representations of the same content (plans as tiles vs. as a table).
 *
 * Each segment is a link rather than a button because the selected
 * representation is part of the URL, so it stays shareable and openable in a
 * new tab.
 */
export const SegmentedControl = ({ className, options }: Props) => (
  <div
    className={clsx(
      "flex w-fit items-center gap-2 rounded-md bg-surface-quaternary p-2",
      className,
    )}
  >
    {options.map(({ to, end, icon, ariaLabel }) => (
      <NavLink
        key={to}
        className={({ isActive }) =>
          clsx(
            "group relative flex size-10 items-center justify-center rounded-md text-foreground-primary transition-colors focus:outline-none",
            isActive ? "bg-surface-secondary" : "hover:bg-surface-tertiary",
          )
        }
        to={to}
        end={end}
        aria-label={ariaLabel}
      >
        {icon}
        <FocusRing />
      </NavLink>
    ))}
  </div>
);
